import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Star } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Review() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [order, setOrder] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/review/${token}`);
        setOrder(data);
        setState("ready");
      } catch (e) {
        setError(e.response?.data?.detail || "That review link isn't valid");
        setState("invalid");
      }
    })();
  }, [token]);

  const submit = async () => {
    setError("");
    if (!rating) {
      setError("Tap a star rating first.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/review/${token}`, { name, rating, text });
      setState("done");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full h-12 bg-transparent border border-white/20 focus:border-volt px-4 font-plex text-xs uppercase tracking-[0.2em] text-white placeholder:text-white/30 outline-none transition-colors duration-300";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <header className="px-6 md:px-10 py-6 border-b border-white/10 flex items-center justify-between">
        <Link to="/">
          <img src="/breethe-logo.webp" alt="Breethe" className="h-8 w-auto" />
        </Link>
        <span className="font-plex text-[10px] uppercase tracking-[0.3em] text-white/40">
          Field report
        </span>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div data-testid="review-page" className="w-full max-w-xl border border-white/10 bg-[#121212] p-8 md:p-12">
          {state === "loading" && (
            <p className="font-plex text-xs uppercase tracking-[0.25em] text-white/40">Loading…</p>
          )}

          {(state === "invalid" || state === "used") && (
            <div data-testid="review-invalid">
              <p className="font-anton uppercase text-3xl md:text-4xl leading-none">No worries.</p>
              <p className="mt-4 text-white/60 font-light">{error}</p>
              <Link
                to="/"
                className="mt-8 inline-block font-plex text-[11px] uppercase tracking-[0.25em] text-volt hover:text-volt-dark transition-colors duration-300"
              >
                Back to Breethe
              </Link>
            </div>
          )}

          {state === "done" && (
            <div data-testid="review-success">
              <p className="font-anton uppercase text-3xl md:text-4xl leading-none">
                Appreciate you<span className="text-volt">.</span>
              </p>
              <p className="mt-4 text-white/60 font-light">
                Your review is in. We read every single one — once we've had a look it'll show up on
                the site as a verified buyer review.
              </p>
              <Link
                to="/"
                data-testid="review-success-home"
                className="mt-8 inline-block font-plex text-[11px] uppercase tracking-[0.25em] text-volt hover:text-volt-dark transition-colors duration-300"
              >
                Back to Breethe
              </Link>
            </div>
          )}

          {state === "ready" && (
            <>
              <p className="font-anton uppercase text-3xl md:text-4xl leading-none">
                How's the breathing<span className="text-volt">?</span>
              </p>
              <p className="mt-3 text-white/60 font-light text-sm md:text-base">
                {order?.plan} — ordered as {order?.email}. Sixty seconds, honest words.
              </p>

              <div className="mt-8 flex gap-2" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    onClick={() => setRating(s)}
                    onMouseEnter={() => setHover(s)}
                    onMouseLeave={() => setHover(0)}
                    data-testid={`review-star-${s}`}
                    className="p-1 transition-transform duration-150 hover:scale-110 active:scale-95"
                    aria-label={`${s} star${s > 1 ? "s" : ""}`}
                  >
                    <Star
                      size={30}
                      className={
                        s <= (hover || rating) ? "text-volt fill-volt" : "text-white/25"
                      }
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>

              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="YOUR NAME — E.G. JOSH M."
                data-testid="review-name-input"
                className={`${inputCls} mt-6`}
              />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="How's it actually working for you? Runs, gym, sleep, snoring — tell us straight."
                data-testid="review-text-input"
                rows={4}
                className="mt-4 w-full bg-transparent border border-white/20 focus:border-volt p-4 font-light text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 resize-none"
              />
              {error && (
                <p data-testid="review-error" className="mt-3 font-plex text-xs text-volt">
                  {error}
                </p>
              )}
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="review-submit-button"
                className="mt-6 w-full h-14 bg-volt text-black font-anton uppercase tracking-wider text-base transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-[0.98] disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send my review"}
              </button>
              <p className="mt-4 font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
                Reviews are checked by a human before they go live
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
